import { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Water Filtration Bundler — Cockpit tool.
 * Given room counts + trade selection, calls the Doctrine bundler and
 * previews the auto-suggested filter/faucet SKUs + subtotal.
 */
const WaterFiltrationModal = ({ open, onClose }) => {
  const [kitchen, setKitchen] = useState(1);
  const [bathroom, setBathroom] = useState(1);
  const [showers, setShowers] = useState(1);
  const [plumbing, setPlumbing] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const compute = async () => {
    setErr(""); setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/doctrine/water-filtration/suggest`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kitchen_count: kitchen,
          bathroom_count: bathroom,
          shower_head_count: showers,
          trades: plumbing ? ["plumbing"] : [],
        }),
      });
      const d = await r.json();
      if (r.ok) setResult(d);
      else setErr(d.detail || `HTTP ${r.status}`);
    } catch { setErr("Network error."); }
    finally { setLoading(false); }
  };

  const num = (label, val, setter, testid) => (
    <div>
      <div style={{ color: "#B8915A", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
      <input type="number" min="0" max="20" data-testid={testid}
             value={val} onChange={(e) => setter(Math.max(0, parseInt(e.target.value) || 0))}
             style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                      border: "1px solid rgba(184,145,90,0.3)", borderRadius: 4,
                      padding: "0.5rem 0.7rem", fontSize: "0.9rem" }} />
    </div>
  );

  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(2,8,20,0.78)",
                  display: "flex", justifyContent: "center", alignItems: "flex-start",
                  padding: "3rem 1.5rem", zIndex: 9998, overflowY: "auto" }}
         data-testid="water-filtration-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "linear-gradient(160deg, #0a1929 0%, #1b2a4e 100%)",
                    border: "1px solid rgba(90,200,216,0.5)", borderRadius: 10,
                    maxWidth: 720, width: "100%", padding: "1.6rem 1.8rem",
                    color: "#F5EFE1", boxShadow: "0 40px 80px rgba(0,0,0,0.5)" }}
           data-testid="water-filtration-modal">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.9rem" }}>
          <div>
            <div style={{ color: "#5AC8D8", fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 800 }}>
              Doctrine · Auto-Bundler
            </div>
            <h2 style={{ margin: "0.2rem 0 0", fontSize: "1.3rem", fontWeight: 700 }}>💧 Water Filtration Kit</h2>
            <div style={{ color: "#ADBED1", fontSize: "0.8rem", marginTop: 3 }}>
              Auto-suggests filters + faucets by room count. Signals NSPIRE water quality without a Tier B plumber.
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="wf-close"
                  style={{ background: "transparent", color: "#5AC8D8", border: "1px solid #5AC8D8",
                           padding: "0.4rem 0.85rem", borderRadius: 4, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>
            Close ✕
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.7rem", marginBottom: "0.9rem" }}>
          {num("Kitchens", kitchen, setKitchen, "wf-kitchen")}
          {num("Bathrooms", bathroom, setBathroom, "wf-bathroom")}
          {num("Shower Heads", showers, setShowers, "wf-showers")}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#F5EFE1", fontSize: "0.85rem", marginBottom: "0.8rem", cursor: "pointer" }}>
          <input type="checkbox" data-testid="wf-plumbing"
                 checked={plumbing} onChange={(e) => setPlumbing(e.target.checked)} />
          Plumbing / Turnover trade in scope (unlocks faucet upgrade bundle)
        </label>

        <button type="button" onClick={compute} disabled={loading}
                data-testid="wf-compute"
                style={{ background: "linear-gradient(90deg, #5AC8D8, #3A9EAD)", color: "#062617",
                         border: "none", padding: "0.55rem 1.2rem", borderRadius: 5,
                         fontSize: "0.86rem", fontWeight: 800,
                         cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
          {loading ? "Calculating…" : "💧 Suggest Bundle"}
        </button>

        {err && <div style={{ marginTop: "0.8rem", color: "#FCA5A5", background: "rgba(220,38,38,0.14)", border: "1px solid rgba(220,38,38,0.4)", padding: "0.5rem 0.8rem", borderRadius: 4, fontSize: "0.82rem" }}>⚠ {err}</div>}

        {result && (
          <div data-testid="wf-result" style={{ marginTop: "1rem" }}>
            <div style={{ color: "#B8915A", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>Suggested Bundle</div>
            {result.lines.map((l) => (
              <div key={l.sku} data-testid={`wf-line-${l.sku}`}
                   style={{ background: "rgba(0,0,0,0.25)", padding: "0.55rem 0.8rem", borderRadius: 4, marginBottom: 5,
                            display: "flex", justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap", borderLeft: "3px solid #5AC8D8" }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ color: "#F3F4F6", fontSize: "0.85rem", fontWeight: 700 }}>
                    {l.name} <span style={{ color: "#ADBED1", fontWeight: 500 }}>· {l.sku}</span>
                  </div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.72rem", marginTop: 2 }}>
                    {l.brand} · qty {l.qty} · {l.labor_min} min labor
                  </div>
                </div>
                <div style={{ color: "#00DC78", fontSize: "0.95rem", fontWeight: 800, whiteSpace: "nowrap" }}>
                  ${l.subtotal.toFixed(2)}
                </div>
              </div>
            ))}
            <div data-testid="wf-total" style={{ marginTop: "0.6rem", display: "flex", justifyContent: "space-between", background: "rgba(0,220,120,0.1)", border: "1px solid rgba(0,220,120,0.4)", padding: "0.6rem 0.85rem", borderRadius: 5, color: "#F5EFE1" }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 700 }}>Hardware subtotal · {result.total_labor_hours}h labor</div>
              <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#00DC78" }}>${result.total_hardware.toFixed(2)}</div>
            </div>
            <div style={{ marginTop: "0.6rem", color: "#ADBED1", fontSize: "0.75rem" }}>
              <strong>Rules applied:</strong>
              <ul style={{ margin: "3px 0 0 1rem", padding: 0 }}>
                {result.rules_applied.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaterFiltrationModal;
